// Sign-in. MSAL in the browser, against the same app registration that owns the API.
//
// THE SHAPE, AND WHY. The registration is MULTI-TENANT because a user's token is
// minted by their OWN home tenant — a single-tenant registration cannot issue one to
// a Crown user at all. Enforcement is SINGLE-TENANT: the authority below names one
// tenant, and the backend independently pins the accepted issuer to the same one. Two
// checks, and only the server's is load-bearing; this one just stops a user from
// signing in with the wrong account and getting an opaque 401.
//
// Never `/common`. That authority would let anyone with any Microsoft account reach a
// sign-in page and be refused only at the API, which reads as a broken app rather than
// as the wrong account.

import { PublicClientApplication, InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser";
import type { AuthConfig } from "../types";

let msal: PublicClientApplication | null = null;
let authConfig: AuthConfig | null = null;

/** Read the sign-in parameters from the server.
 *
 *  Unauthenticated by necessity — it runs before a token exists — and safe: a client
 *  id, a tenant id and a scope name are public values that appear in the redirect URL
 *  of every sign-in. */
export async function loadAuthConfig(): Promise<AuthConfig> {
  if (authConfig) return authConfig;
  const res = await fetch("/api/auth-config");
  if (!res.ok) throw new Error(`auth config unavailable (${res.status})`);
  authConfig = (await res.json()) as AuthConfig;
  return authConfig;
}

/** True when this deployment expects users to sign in.
 *
 *  False locally, where REQUIRE_AUTH defaults off and there may be no app
 *  registration yet — the app then runs with no sign-in screen at all, which is what
 *  makes the UI reviewable before Entra exists. */
export function authRequired(): boolean {
  return !!authConfig?.authRequired;
}

export function clientName(): string {
  return authConfig?.clientName || "HR";
}

/** Initialise MSAL and settle any redirect that just came back.
 *
 *  `handleRedirectPromise` MUST run before anything reads the account list: after
 *  Entra redirects back, the response is sitting in the URL fragment and MSAL has no
 *  account until it has been processed. Reading accounts first shows a signed-in user
 *  the sign-in screen again, which looks like a login loop. */
export async function initAuth(): Promise<AccountInfo | null> {
  const cfg = await loadAuthConfig();
  if (!cfg.authRequired) return null;
  if (!cfg.clientId || !cfg.tenantId) {
    throw new Error("Sign-in is required but this deployment has no client id or tenant configured.");
  }

  msal = new PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: window.location.origin,
    },
    cache: {
      // sessionStorage, not localStorage: a token in localStorage outlives the tab and
      // is shared across every tab on the origin. On a shared HR machine that is a
      // signed-in session the next person inherits. The cost is re-authenticating in a
      // new tab, which is silent when the Entra session cookie is still valid.
      cacheLocation: "sessionStorage",
    },
  });
  await msal.initialize();

  const result = await msal.handleRedirectPromise();
  if (result?.account) {
    msal.setActiveAccount(result.account);
    return result.account;
  }
  const existing = msal.getAllAccounts()[0] ?? null;
  if (existing) msal.setActiveAccount(existing);
  return existing;
}

export function signIn(): void {
  if (!msal || !authConfig) return;
  // select_account forces the account chooser every time, even when the browser
  // already has an active Microsoft session (e.g. a personal or another employer's
  // account signed into this same browser profile). Without it, MSAL silently
  // proceeds with whichever account is already active, which is wrong for a shared
  // HR machine and confusing for anyone signed into more than one Microsoft account.
  void msal.loginRedirect({ scopes: [authConfig.scope], prompt: "select_account" });
}

/** Turn a raw MSAL/Entra error into something safe to show a client's HR staff.
 *
 *  `handleRedirectPromise()` and `acquireTokenSilent()` reject with Entra's own
 *  AADSTS-coded text — internal tenant ids, resource principal ids, trace and
 *  correlation ids. Useful for us in a log, not something to put on a screen a
 *  Crown employee reads. Match on the AADSTS code, which is the one stable part of
 *  the message, and return client-safe copy; fall back to a generic line for
 *  anything unrecognised rather than ever showing the raw text. */
export function friendlyAuthError(raw: string): string {
  if (/AADSTS500011|AADSTS65001/.test(raw)) {
    return "This app hasn't been approved for your organization yet. Ask your IT administrator to complete setup, then try again.";
  }
  if (/AADSTS50020|AADSTS50128|AADSTS90072/.test(raw)) {
    return "That Microsoft account isn't part of this organization. Sign in with your work account.";
  }
  if (/AADSTS50076|AADSTS50079|AADSTS50158/.test(raw)) {
    return "Additional verification is required by your organization. Follow the prompts from Microsoft and try again.";
  }
  if (/AADSTS/.test(raw)) {
    return "Sign-in failed. Try again, or contact your IT administrator if this continues.";
  }
  return "Sign-in is unavailable right now. Try again in a moment.";
}

/** Marks that the NEXT page load is the return leg of a sign-out.
 *
 *  sessionStorage survives navigating away to Microsoft and back within the same tab,
 *  and dies with the tab, which is exactly the lifetime wanted. MSAL clears its own
 *  keys on logout and leaves this one alone. */
const SIGNED_OUT_KEY = "crown-signed-out";

export function signOut(): void {
  if (!msal) return;
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, "1");
  } catch {
    // Storage is unavailable in some privacy modes. The sign-out still works; the
    // confirmation message is the only thing lost.
  }
  // `account` IS WHAT KEEPS MICROSOFT'S PAGE OFF THE SCREEN. Without it, Entra cannot
  // tell which session to end and shows its own "Pick an account" chooser on the way
  // out — the generic page Noah reported. Naming the account makes the logout endpoint
  // a redirect rather than a stop.
  //
  // Still a FULL federated logout, not a local cache clear. Dropping the tokens on our
  // side alone would leave the Entra session cookie alive, so the next person on a
  // shared HR machine could sign straight back in as the last one. That is the same
  // threat the sessionStorage choice above exists for, and it outranks the extra
  // redirect.
  void msal.logoutRedirect({
    account: msal.getActiveAccount() ?? undefined,
    postLogoutRedirectUri: window.location.origin,
  });
}

/** True once, on the page load that follows a sign-out. Clears itself, so a reload
 *  afterwards shows the ordinary sign-in screen rather than repeating the message. */
export function consumeSignedOutFlag(): boolean {
  try {
    const hit = sessionStorage.getItem(SIGNED_OUT_KEY);
    if (hit) sessionStorage.removeItem(SIGNED_OUT_KEY);
    return !!hit;
  } catch {
    return false;
  }
}

export function currentAccount(): AccountInfo | null {
  return msal?.getActiveAccount() ?? null;
}

/** Raised when a token cannot be obtained without showing the user something.
 *
 *  Its own type so the caller can tell "you need to sign in again" from "the network
 *  failed", which are different messages and different recoveries. */
export class NotAuthenticatedError extends Error {
  constructor(message = "Your session has expired. Sign in again to continue.") {
    super(message);
    this.name = "NotAuthenticatedError";
  }
}

/** An access token for our API, refreshed silently when it can be.
 *
 *  Silent acquisition uses the cached refresh token and is the normal path. When it
 *  genuinely needs the user — consent revoked, session expired, MFA — this THROWS
 *  rather than starting a redirect on its own: a redirect fired from inside a chat
 *  turn discards whatever the user was typing. The caller decides when to interrupt. */
export async function getApiToken(): Promise<string | null> {
  if (!authConfig?.authRequired) return null; // local dev, no auth
  if (!msal) throw new NotAuthenticatedError("Sign-in is not ready yet.");
  const account = msal.getActiveAccount();
  if (!account) throw new NotAuthenticatedError();

  try {
    const res = await msal.acquireTokenSilent({ scopes: [authConfig.scope], account });
    return res.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) throw new NotAuthenticatedError();
    throw err;
  }
}

// The Outlook draft path was REMOVED on 2026-09-11, at Crown's request: they do not
// want this product connected to their email at all. What used to live here was
// getGraphMailToken(), which acquired a delegated Mail.ReadWrite token so the SPA could
// create a draft in the signed-in user's own mailbox.
//
// Nothing in this app requests a Microsoft Graph scope now. A finished draft leaves
// through the user's clipboard, which is the only route there has ever been in prod:
// OUTLOOK_DRAFT_ENABLED was absent from prod.bicepparam, so the feature shipped to dev
// and never to Crown. client.test.mjs asserts the absence rather than trusting this
// comment — see "NOTHING in the app can reach a mailbox".
